import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Loader2 } from 'lucide-react';
import { uploadInvoiceFiles } from '../../invoices/api';
import { uploadsFromResponse } from '../../invoices/InvoiceUploader';
import { rememberUploads } from '../../invoices/recentUploads';

/** One tap from Home: camera → upload → the Invoices screen shows it being read. */
export default function QuickInvoicePhoto({ to = '/mobile/invoices' }) {
  const input = useRef(null);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const send = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setError('You are offline. The photo was not sent; try again when you are back online.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const startedAt = Date.now();
      rememberUploads(uploadsFromResponse(list, await uploadInvoiceFiles(list), startedAt));
      navigate(to);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="px-4" data-testid="quick-invoice-photo">
      <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid="quick-invoice-photo-btn"
        className="w-full flex items-center gap-4 min-h-[72px] px-5 rounded-2xl bg-accent-500 text-white shadow-ambient active:scale-[0.99] disabled:opacity-60 motion-organic">
        {busy ? <Loader2 className="w-7 h-7 animate-spin" /> : <Camera className="w-7 h-7" />}
        <span className="text-left">
          <span className="block font-display text-lg leading-tight">{busy ? 'Sending…' : 'Snap an invoice'}</span>
          <span className="block text-sm opacity-90">Photo of a supplier invoice, read for you</span>
        </span>
      </button>
      <input ref={input} type="file" accept="image/*" capture="environment" className="sr-only" tabIndex={-1}
        aria-label="Take a photo of a supplier invoice" data-testid="quick-invoice-photo-input" onChange={(e) => send(e.target.files)} />
      {error && <p className="mt-2 text-sm text-danger" role="alert" data-testid="quick-invoice-photo-error">{error}</p>}
    </div>
  );
}
